/**
 * Advanced error handling with Result patterns, retry logic, and comprehensive error recovery
 */

import { logger } from '../logger/index.js';
import type { LogContext } from '../logger/index.js';
import type {
  Result,
  Ok,
  Err,
  CategorizedError,
  ErrorCategory,
  RetryConfig,
  CircuitBreakerConfig,
  ResultId
} from '../types/common.js';
import { generateResultId } from '../types/branded.js';

// Result factory functions
export const ok = <T>(data: T): Ok<T> => ({
  success: true,
  data,
  id: generateResultId(),
  timestamp: Date.now()
});

export const err = <E = string>(
  error: E, 
  context?: LogContext,
  retryable = false,
  retryAfter?: number
): Err<E> => ({
  success: false,
  error,
  id: generateResultId(),
  timestamp: Date.now(),
  context,
  retryable,
  retryAfter
});

// Re-export types from common module
export type { ErrorCategory, CategorizedError } from '../types/common.js';

// Enhanced error creation
export const createCategorizedError = (
  category: ErrorCategory,
  message: string,
  options: {
    code?: string;
    context?: LogContext;
    originalError?: Error;
    retryable?: boolean;
    retryAfter?: number;
    severity?: 'low' | 'medium' | 'high' | 'critical';
  } = {}
): CategorizedError => {
  const {
    code,
    context,
    originalError,
    retryable = false,
    retryAfter,
    severity = 'medium'
  } = options;

  return {
    category,
    message,
    code,
    context,
    originalError,
    retryable,
    retryAfter,
    severity
  };
};

// Re-export RetryConfig from common module
export type { RetryConfig } from '../types/common.js';

export const defaultRetryConfig: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  jitter: true,
  retryableErrors: ['NETWORK', 'RATE_LIMIT', 'EXTERNAL_SERVICE']
};

// Advanced retry handler with exponential backoff and jitter
export class RetryHandler {
  /**
   * Execute operation with retry logic
   */
  static async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<Result<T, CategorizedError>> {
    const finalConfig = { ...defaultRetryConfig, ...config };
    const sessionId = debug.session.createSession({ operation: 'retry_handler' });
    
    let lastError: CategorizedError | null = null;
    
    for (let attempt = 0; attempt < finalConfig.maxAttempts; attempt++) {
      const attemptId = debug.session.addOperation(sessionId, {
        name: 'retry_attempt',
        context: { attempt: attempt + 1, maxAttempts: finalConfig.maxAttempts }
      });

      try {
        const result = await operation();
        
        debug.session.updateOperation(sessionId, attemptId, {
          status: 'completed',
          endTime: Date.now()
        });

        logger.debug('Retry operation succeeded', {
          attempt: attempt + 1,
          totalAttempts: finalConfig.maxAttempts,
          sessionId
        });

        return ok(result);
      } catch (error) {
        const categorizedError = this.categorizeError(error as Error);
        lastError = categorizedError;

        debug.session.addError(sessionId, error as Error, {
          attempt: attempt + 1,
          category: categorizedError.category,
          retryable: categorizedError.retryable
        });

        debug.session.updateOperation(sessionId, attemptId, {
          status: 'failed',
          endTime: Date.now(),
          error: error as Error
        });

        // Check if error is retryable
        if (!categorizedError.retryable || 
            !finalConfig.retryableErrors.includes(categorizedError.category) ||
            attempt === finalConfig.maxAttempts - 1) {
          break;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = this.calculateDelay(attempt, finalConfig);
        
        logger.warn('Retry operation failed, retrying', {
          attempt: attempt + 1,
          maxAttempts: finalConfig.maxAttempts,
          delay,
          error: categorizedError.message,
          category: categorizedError.category,
          sessionId
        });

        await this.sleep(delay);
      }
    }

    return err(lastError || createCategorizedError('UNKNOWN', 'Operation failed after all retry attempts'));
  }

  /**
   * Categorize error based on type and message
   */
  private static categorizeError(error: Error): CategorizedError {
    const message = error.message.toLowerCase();
    
    // Network errors
    if (message.includes('timeout') || message.includes('econnreset') || 
        message.includes('enotfound') || message.includes('econnrefused')) {
      return createCategorizedError('NETWORK', error.message, {
        originalError: error,
        retryable: true,
        retryAfter: 1000,
        severity: 'medium'
      });
    }

    // Rate limiting
    if (message.includes('rate limit') || message.includes('too many requests') ||
        message.includes('429')) {
      return createCategorizedError('RATE_LIMIT', error.message, {
        originalError: error,
        retryable: true,
        retryAfter: 5000,
        severity: 'medium'
      });
    }

    // Authentication errors
    if (message.includes('unauthorized') || message.includes('401') ||
        message.includes('authentication')) {
      return createCategorizedError('AUTHENTICATION', error.message, {
        originalError: error,
        retryable: false,
        severity: 'high'
      });
    }

    // Authorization errors
    if (message.includes('forbidden') || message.includes('403') ||
        message.includes('permission')) {
      return createCategorizedError('AUTHORIZATION', error.message, {
        originalError: error,
        retryable: false,
        severity: 'high'
      });
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid') ||
        message.includes('400')) {
      return createCategorizedError('VALIDATION', error.message, {
        originalError: error,
        retryable: false,
        severity: 'medium'
      });
    }

    // Database errors
    if (message.includes('database') || message.includes('connection') ||
        message.includes('sql')) {
      return createCategorizedError('DATABASE', error.message, {
        originalError: error,
        retryable: true,
        retryAfter: 2000,
        severity: 'high'
      });
    }

    // Default to unknown
    return createCategorizedError('UNKNOWN', error.message, {
      originalError: error,
      retryable: false,
      severity: 'medium'
    });
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private static calculateDelay(attempt: number, config: RetryConfig): number {
    const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
    const cappedDelay = Math.min(exponentialDelay, config.maxDelay);
    
    if (config.jitter) {
      // Add jitter to prevent thundering herd
      const jitterAmount = cappedDelay * 0.1;
      return cappedDelay + (Math.random() * jitterAmount * 2 - jitterAmount);
    }
    
    return cappedDelay;
  }

  /**
   * Sleep utility
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Re-export CircuitBreakerConfig from common module
export type { CircuitBreakerConfig } from '../types/common.js';

export class CircuitBreaker {
  private static readonly breakers = new Map<string, {
    failures: number;
    lastFailureTime: number;
    state: 'closed' | 'open' | 'half-open';
    config: CircuitBreakerConfig;
  }>();

  /**
   * Execute operation with circuit breaker protection
   */
  static async execute<T>(
    serviceName: string,
    operation: () => Promise<T>,
    config: Partial<CircuitBreakerConfig> = {}
  ): Promise<Result<T, CategorizedError>> {
    const finalConfig: CircuitBreakerConfig = {
      failureThreshold: 5,
      recoveryTimeout: 60000, // 1 minute
      monitoringPeriod: 300000, // 5 minutes
      ...config
    };

    const breaker = this.getOrCreateBreaker(serviceName, finalConfig);
    
    // Check circuit breaker state
    if (breaker.state === 'open') {
      if (Date.now() - breaker.lastFailureTime > finalConfig.recoveryTimeout) {
        breaker.state = 'half-open';
        logger.info('Circuit breaker transitioning to half-open', { serviceName });
      } else {
        return err(createCategorizedError('EXTERNAL_SERVICE', 
          `Circuit breaker is open for ${serviceName}`, {
            code: 'CIRCUIT_BREAKER_OPEN',
            retryable: true,
            retryAfter: finalConfig.recoveryTimeout - (Date.now() - breaker.lastFailureTime),
            severity: 'high'
          }));
      }
    }

    try {
      const result = await operation();
      
      // Success - reset circuit breaker
      if (breaker.state === 'half-open') {
        breaker.state = 'closed';
        breaker.failures = 0;
        logger.info('Circuit breaker closed after successful operation', { serviceName });
      }
      
      return ok(result);
    } catch (error) {
      breaker.failures++;
      breaker.lastFailureTime = Date.now();
      
      if (breaker.failures >= finalConfig.failureThreshold) {
        breaker.state = 'open';
        logger.error('Circuit breaker opened due to failures', {
          serviceName,
          failures: breaker.failures,
          threshold: finalConfig.failureThreshold
        });
      }
      
      const categorizedError = RetryHandler['categorizeError'](error as Error);
      return err(categorizedError);
    }
  }

  private static getOrCreateBreaker(serviceName: string, config: CircuitBreakerConfig) {
    if (!this.breakers.has(serviceName)) {
      this.breakers.set(serviceName, {
        failures: 0,
        lastFailureTime: 0,
        state: 'closed',
        config
      });
    }
    return this.breakers.get(serviceName)!;
  }

  /**
   * Get circuit breaker status
   */
  static getStatus(serviceName: string): {
    state: 'closed' | 'open' | 'half-open';
    failures: number;
    lastFailureTime: number;
  } | null {
    const breaker = this.breakers.get(serviceName);
    if (!breaker) return null;
    
    return {
      state: breaker.state,
      failures: breaker.failures,
      lastFailureTime: breaker.lastFailureTime
    };
  }
}

// Error recovery strategies
export interface RecoveryStrategy<T> {
  readonly name: string;
  readonly canRecover: (error: CategorizedError) => boolean;
  readonly recover: (error: CategorizedError, context?: LogContext) => Promise<Result<T, CategorizedError>>;
}

export class ErrorRecoveryManager {
  private static readonly strategies = new Map<string, RecoveryStrategy<any>>();

  /**
   * Register a recovery strategy
   */
  static registerStrategy<T>(strategy: RecoveryStrategy<T>): void {
    this.strategies.set(strategy.name, strategy);
    logger.debug('Recovery strategy registered', { strategyName: strategy.name });
  }

  /**
   * Attempt to recover from an error
   */
  static async attemptRecovery<T>(
    error: CategorizedError,
    context?: LogContext
  ): Promise<Result<T, CategorizedError>> {
    const applicableStrategies = Array.from(this.strategies.values())
      .filter(strategy => strategy.canRecover(error));

    if (applicableStrategies.length === 0) {
      return err(error);
    }

    logger.info('Attempting error recovery', {
      errorCategory: error.category,
      applicableStrategies: applicableStrategies.map(s => s.name),
      ...context
    });

    for (const strategy of applicableStrategies) {
      try {
        const result = await strategy.recover(error, context);
        if (result.success) {
          logger.info('Error recovery successful', {
            strategyName: strategy.name,
            errorCategory: error.category,
            ...context
          });
          return result;
        }
      } catch (recoveryError) {
        logger.warn('Recovery strategy failed', {
          strategyName: strategy.name,
          error: (recoveryError as Error).message,
          ...context
        });
      }
    }

    return err(error);
  }
}

// Result utilities
export class ResultUtils {
  /**
   * Map over successful results
   */
  static map<T, U>(result: Result<T>, fn: (data: T) => U): Result<U> {
    if (result.success) {
      return ok(fn(result.data));
    }
    return result;
  }

  /**
   * Chain operations that return Results
   */
  static async chain<T, U>(
    result: Result<T>,
    fn: (data: T) => Promise<Result<U>>
  ): Promise<Result<U>> {
    if (result.success) {
      return await fn(result.data);
    }
    return result;
  }

  /**
   * Handle both success and error cases
   */
  static match<T, U, E>(
    result: Result<T, E>,
    onSuccess: (data: T) => U,
    onError: (error: E) => U
  ): U {
    if (result.success) {
      return onSuccess(result.data);
    }
    return onError(result.error);
  }

  /**
   * Convert Result to Promise (throws on error)
   */
  static toPromise<T, E>(result: Result<T, E>): Promise<T> {
    if (result.success) {
      return Promise.resolve(result.data);
    }
    return Promise.reject(new Error(typeof result.error === 'string' ? result.error : JSON.stringify(result.error)));
  }

  /**
   * Convert Promise to Result
   */
  static async fromPromise<T>(promise: Promise<T>): Promise<Result<T, CategorizedError>> {
    try {
      const data = await promise;
      return ok(data);
    } catch (error) {
      const categorizedError = RetryHandler['categorizeError'](error as Error);
      return err(categorizedError);
    }
  }
}

// Export main error handling utilities
export const errorHandling = {
  result: {
    ok,
    err,
    map: ResultUtils.map,
    chain: ResultUtils.chain,
    match: ResultUtils.match,
    toPromise: ResultUtils.toPromise,
    fromPromise: ResultUtils.fromPromise
  },
  retry: RetryHandler,
  circuitBreaker: CircuitBreaker,
  recovery: ErrorRecoveryManager,
  createError: createCategorizedError
};

// Register default recovery strategies
ErrorRecoveryManager.registerStrategy({
  name: 'network-retry',
  canRecover: (error) => error.category === 'NETWORK' && error.retryable,
  recover: async (error, context) => {
    logger.info('Attempting network retry recovery', { ...context });
    // Implement network-specific recovery logic
    return err(error); // Placeholder
  }
});

ErrorRecoveryManager.registerStrategy({
  name: 'rate-limit-wait',
  canRecover: (error) => error.category === 'RATE_LIMIT' && error.retryAfter,
  recover: async (error, context) => {
    if (error.retryAfter) {
      logger.info('Waiting for rate limit reset', { 
        retryAfter: error.retryAfter,
        ...context 
      });
      await new Promise(resolve => setTimeout(resolve, error.retryAfter!));
      return err(error); // Placeholder - would retry original operation
    }
    return err(error);
  }
});
